import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ComponentFactoryResolver,
  EventEmitter,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import { get, merge } from 'lodash';
import { Subject, Subscription } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';

import {
  TestSetupComponent,
  ITestSetupEntry,
  BaseSetupTab,
  TestSetupTabsComponent,
  SaveTemplateComponent,
  TemplateService
} from '@nrwl/test-setup';
import { NavigationProvider } from '@nrwl/navigation-abstract';
import {
  DialogService,
  NotificationService,
  TEST_STATE,
  TestListService,
  TestTypeValue,
  ValuesExchangeProvider
} from '@nrwl/common-lib';
import { ServiceSetupService } from './service-setup.service';
import { ServiceSetupBasicTabComponent } from './tabs/service-setup-basic-tab/service-setup-basic-tab.component';
import { ServiceSetupAdvanceTabComponent } from './tabs/service-setup-adavance-tab/service-setup-advance-tab.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TemplateTabsComponent } from '@nrwl/template-management';
import { ServiceConfigFieldMapping } from './json/service-agent-model';
@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'service-setup',
  providers: [ServiceSetupService],
  template: '<nrwl-test-setup-tabs [parentObj]="currentObj"></nrwl-test-setup-tabs>'
})
export class ServiceTestSetupComponent
  extends TestSetupComponent
  implements OnInit, AfterViewInit, OnDestroy, ITestSetupEntry
{
  @ViewChild(TestSetupTabsComponent, { static: true }) testSetupTabRef: TestSetupTabsComponent;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  setupConfig: any;
  //eslint-disable-next-line
  agentBasedConfig: any;
  setupId: string;
  testId: string;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  onClose = new EventEmitter<any>();
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  dataId: any;
  router: Router;
  setupService: ServiceSetupService;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  openWizard: any;
  editMode: boolean;
  subscription: Subscription;
  changeDetectorRef: ChangeDetectorRef;
  activatedRouteSubscription: Subscription;
  currentObj: any; //eslint-disable-line
  editDeviceDialogRef: MatDialogRef<SaveTemplateComponent>;
  templateDialogRef: MatDialogRef<TemplateTabsComponent>;
  monitorCountResponse: { activeMonitorsCount: number; monitorMaxLimit: number };
  private readonly unsubscribe: Subject<void> = new Subject();
  constructor(
    router: Router,
    setupService: ServiceSetupService,
    changeDetectorRef: ChangeDetectorRef,
    private readonly exchange: ValuesExchangeProvider,
    private readonly activatedRoute: ActivatedRoute,
    private readonly factoryResolver: ComponentFactoryResolver,
    private readonly navigationProvider: NavigationProvider,
    private readonly dialogService: DialogService,
    private readonly notificationService: NotificationService,
    private readonly testListService: TestListService,
    private readonly templateService: TemplateService,
    private dialog: MatDialog
  ) {
    super();
    this.router = router;
    this.setupService = setupService;
    this.changeDetectorRef = changeDetectorRef;
  }

  ngOnInit() {
    this.currentObj = this;
    this.activatedRouteSubscription = this.activatedRoute.queryParams.subscribe(data => {
      if (this.testId !== this.setupId) {
        const defaultParams = merge({}, this.dataId, JSON.parse(get(data, 'defaultParams', '{}')));
        this.setupService.openTestSetup(
          this.setupId,
          this.setupConfig,
          defaultParams,
          null,
          null,
          this.agentBasedConfig
        );
        this.testId = this.setupId;
      }
    });
    this.exchange.notifyListeners('new_test', { noValue: true });
    this.templateService.closeDialog?.subscribe(status => {
      if (status && this.templateDialogRef) {
        this.templateDialogRef.close();
      }
    });
  }

  ngAfterViewInit() {
    this.createContentComponent(1);
    this.changeDetectorRef.detectChanges();
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.activatedRouteSubscription) {
      this.activatedRouteSubscription.unsubscribe();
    }
    if (this.unsubscribe) {
      this.unsubscribe.unsubscribe();
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  get(object: any, path: string): any {
    return get(object, path);
  }

  //-------------- Control && Validation -----------------------
  toggleTabChange(index: number): void {
    this.setupService.toggleTabChange(index);
    const tabIndex = index + 1;
    // create the component only once when its not available
    if (!this.testSetupTabRef['contentRef' + `${tabIndex}`].length) {
      this.createContentComponent(tabIndex);
    }
  }

  toggleClose(): void {
    this.setupService.toggleClose(() => this.onClose.emit(true));
  }

  toggleReOpen(): void {
    this.setupService.reOpen();
  }

  toggleFinish(): void {
    this.setupService.onNext();
    const lmapObject = this.setupService.createLMAPObject(0);
    this.editMode = get(this.setupService, 'editMode', false);
    if (this.editMode) {
      this.setupService.openTestStatusDialog('Updating service', 'Update');
      const scheduleId = this.setupService.defaultParamsById['scheduleId'];
      const testStatus = this.testListService.getTestListById(scheduleId)?.getTestStatus();
      const applyValue = testStatus === TEST_STATE.RUNNING ? 'next-run-restart-now' : 'next-run';
      lmapObject['runlater'] = 'yes';
      this.subscription = this.setupService
        ._updateControllerTest({
          opt: {
            id: this.setupService.defaultParamsById['scheduleId'],
            apply: applyValue
          },
          config: lmapObject
        })
        .subscribe(
          response => {
            const message = 'Service test has been updated';
            this.notificationService.onSuccess(message);
            this.testListService.updateTestResponse();
            this.setupService.dialogRef.close();
            this.toggleClose();
            this.exchange.notifyListeners('new_test', response);
          },
          err => {
            const message = 'Failed to update service test';
            this.notificationService.onFail(message);
            this.setupService.dialogRef.close();
            this.toggleClose();
          }
        );
    } else {
      this.setupService.saveSetupData();
      this.exchange.registerInterest('new_test', info => {
        if (info && !info.noValue) {
          this.setupService.toggleClose(() => this.onClose.emit(true));
        }
      });
    }
    // this.exchange.registerInterest('new_test', info => {
    //   if (info && !info.isRequestFailed) {
    //     this.setupService.toggleClose(() => this.onClose.emit(true));
    //   }
    // });
  }

  saveTestConfig(): void {
    this.setupService.onNext();
    const thresholdConfig = {};
    thresholdConfig['severityThresholds'] = [
      {
        severity: 'critical',
        threshold: this.setupService.output[ServiceConfigFieldMapping.THRESHOLD_CRITICALL]
      },
      {
        severity: 'warning',
        threshold: this.setupService.output[ServiceConfigFieldMapping.THRESHOLD_WARNING]
      }
    ];
    const configParams = this.setupService._getTemplateConfigParameters();
    const testParams = { ...configParams, ...thresholdConfig };
    const allTestParams = this.setupService._createTemplateFormat(
      testParams,
      this.setupService.output.selectedOriginator,
      null,
      this.setupService.output.selectedApp
    );
    this.editDeviceDialogRef = this.dialog.open(SaveTemplateComponent, {
      width: '70rem',
      disableClose: true,
      data: {
        deviceInfo: allTestParams,
        editMode: true,
        testType: TestTypeValue.SERVICE_CONTINUITY
      }
    });
  }

  openTemplates(): void {
    const param = {
      id: 'setupService.test_templates',
      callBackService: this,
      callBackFuntion: this.openTemplatesDialog
    };
    this.dialogService.showDialog('confirm', 'body', param);
  }

  // eslint-disable-next-line
  openTemplatesDialog(service: any): void {
    service.toggleClose();

    service.templateDialogRef = service.dialog.open(TemplateTabsComponent, {
      width: '100rem',
      minHeight: '50rem',
      height: 'calc(100vh - 20px)',
      disableClose: true,
      data: {
        filterType: TestTypeValue.SERVICE_CONTINUITY,
        myTemplatesColumnHeaders: ['name', 'accessLevel', 'lastModified', 'selectActions'],
        sharedTemplatesColumnHeaders: [
          'name',
          'accessLevel',
          'owner',
          'lastModified',
          'selectActions'
        ]
      }
    });
  }

  downloadTestConfig(): void {
    this.setupService.onNext();
    const lmapObject = this.setupService.getBulkPayload();
    this.setupService._downloadTestConfig(lmapObject);
  }

  getSetupService(): ServiceSetupService {
    return this.setupService;
  }
  getSetupTab(): BaseSetupTab {
    return this.setupService.currentTab;
  }

  getFactoryResolver(): ComponentFactoryResolver {
    return this.factoryResolver;
  }

  getContentViewRef(index: number): ViewContainerRef {
    return this.testSetupTabRef['contentRef' + `${index}`];
  }

  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  resolveComponentType(pageId: string): any {
    switch (pageId) {
      case 'service.setup.html':
        return ServiceSetupBasicTabComponent;
      case 'service.advanced.html':
        return ServiceSetupAdvanceTabComponent;
      default: {
        console.error('Unrecognized template name:', pageId);
        return null;
      }
    }
  }
}
